<template>
  <div ref="contentOrgEl" class="content-organization">
    <!-- plus free access callout -->
    <plus-free-access-callout
      v-if="userAuthStore.plusFreeAccess.enabled"
      tour-id="plusFreeAccessSurpriseMe"
    />

    <search-header
      title="Similar Workout Videos"
      active="filters"
      filter-url="/videos"
      :show-tags="true"
      :show-toggle="false"
      @tag-update="openExitModal"
      @clear-all="openExitModal"
    />

    <div ref="contentResultsEl" class="content-type-options">
      <!-- No filters on this view -->

      <div class="content-results">
        <!-- error state, modal will also show -->
        <callout v-if="errorMsg" type="error">
          <div>{{ errorMsg }}</div>
        </callout>

        <!-- result top bar -->
        <div class="results-details">
          <search-sort
            :show-filter-toggle="false"
            :total="searchResults.total"
            @sort-update="sortUpdate"
          />
          <search-result-actions
            :active-expander="activeExpander"
            :accessibility-expander-id="accessibilityExpanderId"
          >
            <div class="action">
              <icon-button
                type="button"
                icon="pause_circle_outline"
                label="Similar"
                :rotate-icon="90"
                classes="surprise"
                :icons-outlined="false"
                :aria-expanded="activeExpander === 'similar'"
                :aria-controls="accessibilityExpanderId"
                @click-action="toggleActiveExpander('similar')"
              />
            </div>
          </search-result-actions>
        </div>

        <!-- expander -->
        <div :id="accessibilityExpanderId" class="results-expander" :aria-hidden="!activeExpander">
          <video-similar-filter
            v-if="userAuthStore.features.SIMILAR_FILTERS && activeExpander === 'similar'"
            :video="currVideo!"
            :dropdown-filter="dropdownFilters.similarTime"
            @close-action="toggleActiveExpander"
            @filter-update="tagUpdate"
          />
        </div>

        <!-- results -->
        <search-result-cards
          v-if="!errorMsg && (searchResults.loading || searchResults.total > 0)"
          v-slot="{ cardOrder }"
          :loading="searchResults.loading"
          :per-page-total="searchResults.perPage"
          :current-page-total="searchResultsData.length"
          entity-type="workout"
        >
          <video-card
            v-for="(video, index) in searchResultsData"
            :key="video.id"
            :video="video"
            :plus-pass="searchStore.plusPass"
            :cheapest-monthly-price="searchStore.cheapestMonthlyPrice"
            :style="{ order: cardOrder(index) }"
          />
        </search-result-cards>
        <search-no-results v-else @clear-all="clearAll" />

        <!-- pagination -->
        <pagination
          :current-page="searchResults.currentPage"
          :total-pages="searchResults.totalPages"
          :total-items="searchResults.total"
          :per-page="searchResults.perPage"
          @page-update="pageUpdate"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, onBeforeUnmount, ref, shallowRef, watch } from 'vue'
import { useRoute } from 'vue-router'
import Pagination from '../search/pagination.vue'
import SearchHeader from '@/components/search/search-header.vue'
import SearchSort from '@/components/search/search-sort.vue'
import SearchResultActions from '@/components/search/search-result-actions.vue'
import { useUserAuthStore } from '@/stores/user-auth'
import { useModalsStore } from '@/stores/modals'
import { useSearchStore } from '@/stores/search'
import type { SimilarVideoType, VideoCardType } from '@/types/model-types'
import { removeArraySyntax } from '@/utils/helpers'
import { fbApi, isAbortError, objToSearchParams } from '@/utils/http-api'
import type { DropdownFilter, SortOption, VideoFacets, VideoPagination } from '@/types/search-types'
import Callout from '@/components/notifications/callout.vue'
import SearchResultCards from '@/components/search/search-result-cards.vue'
import VideoCard from '@/components/videos/video-card.vue'
import VideoSimilarFilter from './video-similar-filter.vue'
import SearchNoResults from '@/components/search/search-no-results.vue'
import { useSearchRoute } from '@/composables/search-route'
import PlusFreeAccessCallout from '@/components/notifications/plus-free-access-callout.vue'
import IconButton from '@/components/buttons/icon-button.vue'

/** TYPES **/
type ActiveExpander = 'similar' | ''

/** STATE **/
const route = useRoute()
const userAuthStore = useUserAuthStore()
const modalsStore = useModalsStore()
const searchStore = useSearchStore()
const { contentResultsEl, contentOrgEl, tagUpdate, sortUpdate, clearAll, pageUpdate } =
  useSearchRoute()

const page = ref<{
  loading: boolean
  error: string
}>({
  loading: false,
  error: '',
})

const searchResults = ref<{
  loading: boolean
  error: string
  currentPage: number
  totalPages: number
  total: number
  perPage: number
}>({
  loading: false,
  error: '',
  currentPage: 0,
  totalPages: 0,
  perPage: 20,
  total: 0,
})

const searchResultsData = shallowRef<VideoCardType[]>([])

let currVideo = shallowRef<SimilarVideoType | undefined>(undefined)

const activeExpander = ref<ActiveExpander>('similar')

/** NON REACTIVE **/
let fetchAbort: AbortController | null = null

const accessibilityExpanderId = 'results-expander-control'
const modalId = 'find-similar-exit-modal'

const dropdownFilters: Record<string, DropdownFilter> = {
  similarTime: {
    label: 'Similar Time',
    name: 'time',
    multiple: false,
    value: '',
    // computed values, see model/Video.php->toSearchArray()
    items: [
      { label: 'Similar Workouts', value: 'same' },
      { label: 'Shorter Similar Workouts', value: 'shorter' },
      { label: 'Longer Similar Workouts', value: 'longer' },
    ],
  },
}

const sortOptions: SortOption[] = [
  { label: 'Newest First', value: 'newest' },
  { label: 'Most Popular', value: 'popular' },
  { label: 'Shortest', value: 'shortest' },
  { label: 'Longest', value: 'longest' },
  { label: 'Oldest', value: 'oldest' },
  { label: 'Relevance', value: 'relevance' },
]

/** COMPUTED **/
const errorMsg = computed(() => page.value.error || searchResults.value.error)

/** WATCHERS **/
watch(
  () => route.query,
  () => {
    // resync on every query string change
    syncUrlToState({ init: false })

    // refresh data
    fetchVideos()
  },
)

// handles the exit modal confirmation
watch(
  () => modalsStore.simpleModal.action,
  (newAction, prevAction) => {
    // confirm action
    if (
      modalsStore.simpleModal.id === modalId &&
      newAction !== prevAction &&
      newAction === 'confirm'
    ) {
      viewAllVideos()
      return
    }

    // close action
    if (
      modalsStore.simpleModal.id === modalId &&
      newAction !== prevAction &&
      newAction === 'close'
    ) {
      closeExitModal()
      return
    }
  },
)

/** LIFECYCLE **/
onBeforeMount(async () => {
  // clear everything
  searchStore.$reset()

  searchStore.setSortOptions(sortOptions)

  syncUrlToState({ init: true })

  // do not wait for this to finish before moving on
  fetchVideos()
})

// cancel fetch request, especially if going to browse view as it will
// as it will use the search store
onBeforeUnmount(() => {
  if (fetchAbort) {
    fetchAbort.abort()
  }
})

/** METHODS **/

/**
 * Update pinia based on url query params as the source of truth.
 * Clear out values and reset them based on the query params
 */
function syncUrlToState({ init }: { init: boolean }) {
  // removes [] and forces array for items with [] in var names
  const queryParams = removeArraySyntax(route.query)

  // will be passed in the url
  searchStore.setSimilarToId(Number(queryParams.similarto))

  // filters
  searchStore.setDropdownFilters({
    dropdownFilters,
    queryStringFilters: queryParams,
    dropdownUrlParameterNames: {
      time: 'similarTime',
    },
    init,
  })

  // no checkbox, range, or keyword search options for this view

  searchStore.setSortSelected(queryParams.sort || 'newest')

  // rebuild tags last, after all pinia state is loaded
  searchStore.setInitialTags()
}

async function fetchVideos() {
  // clear any error
  searchResults.value.error = ''
  searchResults.value.loading = true

  // format GET query params
  let params = route.query || {}
  params = removeArraySyntax(params)

  // reset pagination on a filter change
  if (!('page' in params)) {
    searchResults.value.currentPage = 1
  }

  try {
    // cancel any previous fetch
    if (fetchAbort) {
      fetchAbort.abort()
    }

    // re-init abort controller
    fetchAbort = new AbortController()

    const apiUrl = '/videos/similar'

    const res = await fbApi.get(apiUrl, {
      searchParams: objToSearchParams(params),
      signal: fetchAbort.signal,
    })

    if (!res || !res.body || !res.body.data) {
      throw new Error(`Unable to fetch workouts, please refresh the page and try again`)
    }

    const {
      selectedVideo,
      paginatedVideos,
      facets,
    }: {
      selectedVideo: SimilarVideoType
      paginatedVideos: VideoPagination
      facets: VideoFacets
    } = res.body.data

    // video that shows in the similar expander
    // only change if video actually changes (which it shouldn't)
    if (!currVideo.value || currVideo.value.id !== selectedVideo.id) {
      currVideo.value = selectedVideo
    }

    searchResults.value.total = paginatedVideos.total
    searchResults.value.currentPage = paginatedVideos.current_page
    searchResults.value.totalPages = paginatedVideos.last_page
    searchResultsData.value = paginatedVideos.data

    // pinia facets
    searchStore.updateFacets(facets)
  } catch (err) {
    // skip if Aborted
    if (isAbortError(err)) {
      console.warn('fetchVideos aborted')
      return
    }

    // oops model will show
    console.error(err)
    searchResults.value.error = `Unable to fetch workouts, please refresh the page and try again`
  } finally {
    searchResults.value.loading = false
  }
}

// exit confirmation
function openExitModal() {
  modalsStore.openSimpleModal({
    type: 'CONFIRM',
    id: modalId,
    props: {
      heading: 'Leave Similar Workouts',
      subheading: 'Removing the similar filters will redirect to all videos. Are you sure?',
      buttonText: 'Yes',
      cancelText: 'No',
    },
  })
}

function closeExitModal() {
  // re-add the time tag back if user chooses to stay on the page
  // we can get this from the URL because the route was not updated
  // when user attempted to remove filter (and modal open)
  // NOTE: this.apiParameters has already been updated, so we cannot use that
  const routeParams = route.query
  const similarTime = dropdownFilters.similarTime

  const selectedOption = similarTime.items!.find((item: any) => item.value === routeParams.time)

  if (selectedOption) {
    const tag = searchStore.dropdownTagItem({
      id: similarTime.name,
      filterName: similarTime.name,
      tagLabel: selectedOption && selectedOption.label,
      value: selectedOption && selectedOption.value,
    })

    searchStore.handleTagUpdate(tag)

    // reset dropdown state
    searchStore.setDropdownValue({
      filterName: similarTime.name,
      value: selectedOption.value,
    })
  }
}

function viewAllVideos() {
  window.location.href = '/videos'
}

function toggleActiveExpander(expander: ActiveExpander) {
  if (activeExpander.value === expander) {
    activeExpander.value = ''
    return
  }

  activeExpander.value = expander
}
</script>
